import type { Language } from '@/hooks/queries/use-get-user-languages.query';
import type { WorkPreference } from '@/hooks/queries/use-get-user-work-preferences.query';
import axios from '@/lib/axios';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

export interface Address {
  id: string;
  address: string;
  city: string;
  zipcode: string;
  state: string;
  country: string;
  default_address: boolean;
  created_at: string;
  updated_at: string;
  address_name: string | null;
  apt_suite_unit: string | null;
  bedroom: number;
  bathroom: number;
  people: number;
  pet: number;
  cleaning_area: string | null;
  house_desc: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface UserDetails {
  userid: string;
  firstname: string;
  middlename: string | null;
  lastname: string;
  dob: string | null;
  ssn: string | null;
  email: string;
  phone: string;
  addresses: Address[];
  role: string;
  is_verified: boolean;
  is_active: boolean;
  availability: unknown[];
  distance_to_travel: number;
  work_preference: WorkPreference[];
  biography: string | null;
  profile_pic: string | null;
  languages: Language[];
  basic_cleaning_supplies_image: string | null;
  tools_equipment_image: string | null;
  disposable_items_image: string | null;
  optional_specialty_items_image: string | null;
  latLng?: {
    latitude: number;
    longitude: number;
  };
  total_ratings: number;
  average_rating: number;
  total_completed_jobs: number;
  auto_accept_enabled?: boolean;
}

interface DefaultAddress {
  id: string | null;
  address: string | null;
  city: string | null;
  zipcode: string | null;
  state: string | null;
  country: string | null;
  default_address: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  address_name: string | null;
  bedroom: number | null;
  bathroom: number | null;
  people: number | null;
  pet: number | null;
  cleaning_area: string | null;
  house_desc: string | null;
}

export type CleanerAddress = DefaultAddress;

export interface UserDetailsResponse {
  message: string;
  details: UserDetails;
  default_address: DefaultAddress;
}

const getUserDetailsQuery = async (
  userid: string
): Promise<UserDetailsResponse> => {
  const response = await axios.get(`/user/details/${userid}/`);
  return response.data;
};

export const useGetUserDetailQuery = (
  userid: string,
  options?: Partial<UseQueryOptions<UserDetailsResponse>>
) => {
  return useQuery({
    queryKey: ['user', 'details', userid],
    queryFn: () => getUserDetailsQuery(userid),
    retry: false,
    enabled: !!userid && userid.trim().length > 0,
    ...options,
  });
};
